import { useCallback, useEffect, useRef, useState } from 'react';
import { useYieldCallback } from '@zillow/yield-callback';
import type { DeviceInfo, PositionCalculation, TooltipPosition, TooltipPositionStrategy, TooltipSize } from './types';

const VIEWPORT_PADDING = 16;

// Utility to find the actual scrolling container
const getScrollParent = (element: Element | null): Element => {
  if (!element || typeof window === 'undefined') {
    return document.documentElement;
  }

  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    const style = getComputedStyle(parent);
    const overflow = style.overflow + style.overflowY + style.overflowX;

    if (/(auto|scroll)/.test(overflow)) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return document.documentElement;
};

// Modern device detection using feature detection - industry standard approach
export const useDeviceDetection = (): DeviceInfo => {
  // Default to desktop during SSR for better UX (tooltips work better on desktop)
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: false,
    isTouch: false,
  });

  useEffect(() => {
    // Only run on client-side after hydration
    if (typeof window !== 'undefined') {
      // Feature detection - test what the device CAN do, not what it claims to be
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isMobile = isTouch || window.innerWidth < 768;

      setDeviceInfo({
        isMobile,
        isTouch,
      });
    }
  }, []);

  return deviceInfo;
};

// Desktop-only tooltip hook with clean hover behavior
export const useDesktopTooltip = (
  preferredPosition: TooltipPosition = 'above',
  offset: number,
  size: TooltipSize = 'md',
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>(preferredPosition);
  const [coordinates, setCoordinates] = useState<{ top: number; left: number } | null>(null);
  const [arrowOffset, setArrowOffset] = useState(0);

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Calculate optimal position for desktop
  const calculatePosition = useCallback((): PositionCalculation | null => {
    if (!triggerRef.current || typeof window === 'undefined') return null;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Try to get tooltip dimensions, with better fallback estimates
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    // Increased estimates for more realistic tooltip sizes (especially for TotalMonthlyPrice)
    const estimatedTooltipWidth = Math.min(320, viewportWidth * 0.8); // Responsive estimate
    const estimatedTooltipHeight = size === 'auto' ? 40 : 120;
    const tooltipWidth = tooltipRect?.width && tooltipRect.width > 0 ? tooltipRect.width : estimatedTooltipWidth;
    const tooltipHeight = tooltipRect?.height && tooltipRect.height > 0 ? tooltipRect.height : estimatedTooltipHeight;

    // Determine vertical position based on available space
    let finalPosition = preferredPosition;
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;

    if (preferredPosition === 'above' && spaceAbove < tooltipHeight + offset + VIEWPORT_PADDING) {
      if (spaceBelow > tooltipHeight + offset + VIEWPORT_PADDING) {
        finalPosition = 'below';
      }
    } else if (preferredPosition === 'below' && spaceBelow < tooltipHeight + offset + VIEWPORT_PADDING) {
      if (spaceAbove > tooltipHeight + offset + VIEWPORT_PADDING) {
        finalPosition = 'above';
      }
    }

    // Calculate horizontal position
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    let left = triggerCenterX - tooltipWidth / 2;

    // Adjust for viewport boundaries
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    } else if (left + tooltipWidth > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - VIEWPORT_PADDING - tooltipWidth;
    }

    // Calculate arrow offset: distance from tooltip center to trigger center
    const tooltipCenterX = left + tooltipWidth / 2;
    const calculatedArrowOffset = triggerCenterX - tooltipCenterX;

    // Calculate vertical position
    let top: number;
    if (finalPosition === 'above') {
      top = triggerRect.top - tooltipHeight - offset;
    } else {
      top = triggerRect.bottom + offset;
    }

    return {
      position: finalPosition,
      coordinates: { top, left },
      arrowOffset: calculatedArrowOffset,
    };
  }, [preferredPosition, offset]);

  // Open tooltip with robust positioning
  const openTooltip = useCallback(() => {
    setIsOpen(true);
    setPosition(preferredPosition);
    setCoordinates({ top: 0, left: 0 }); // Temporary coordinates

    // Retry position calculation until tooltip is properly rendered
    const calculateWithRetry = (attempts = 0) => {
      const maxAttempts = 3;

      requestAnimationFrame(() => {
        const calculation = calculatePosition();

        // Check if we got valid dimensions
        const tooltipRect = tooltipRef.current?.getBoundingClientRect();
        const hasValidDimensions = tooltipRect && tooltipRect.width > 0;

        if (calculation && (hasValidDimensions || attempts >= maxAttempts)) {
          setPosition(calculation.position);
          setCoordinates(calculation.coordinates);
          setArrowOffset(calculation.arrowOffset);
        } else if (attempts < maxAttempts) {
          // Retry if tooltip hasn't rendered properly yet
          calculateWithRetry(attempts + 1);
        }
      });
    };

    calculateWithRetry();
  }, [preferredPosition, calculatePosition]);

  // Close tooltip
  const closeTooltip = useCallback(() => {
    setIsOpen(false);
    setCoordinates(null);
    setArrowOffset(0);
  }, []);

  // Handle mouse enter with optional delay
  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    openTooltip();
  }, [openTooltip]);

  // Handle mouse leave with optional delay
  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    closeTooltip();
  }, [closeTooltip]);

  // Handle keyboard activation (Enter/Space)
  const handleKeyDown = useYieldCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (isOpen) {
          closeTooltip();
        } else {
          openTooltip();
        }
      }
    },
    [isOpen, openTooltip, closeTooltip],
  );

  // Handle window resize to reposition tooltip
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      const calculation = calculatePosition();
      if (calculation) {
        setPosition(calculation.position);
        setCoordinates(calculation.coordinates);
        setArrowOffset(calculation.arrowOffset);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, calculatePosition]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    isOpen,
    position,
    coordinates,
    arrowOffset,

    // Refs
    triggerRef,
    tooltipRef,

    // Handlers
    handleMouseEnter,
    handleMouseLeave,
    handleKeyDown,

    // Manual controls
    openTooltip,
    closeTooltip,
  };
};

// Mobile-only tooltip hook with tap-to-toggle behavior
export const useMobileTooltip = (
  preferredPosition: TooltipPosition = 'below',
  offset: number,
  positionStrategy: TooltipPositionStrategy = 'viewport-center',
  size: TooltipSize = 'md',
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>(preferredPosition);
  const [coordinates, setCoordinates] = useState<{ top: number; left: number } | null>(null);
  const [arrowOffset, setArrowOffset] = useState(0);

  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Calculate optimal position for mobile with viewport awareness
  const calculatePosition = useCallback((): PositionCalculation | null => {
    if (!triggerRef.current || typeof window === 'undefined') return null;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Try to get tooltip dimensions, with better fallback estimates for mobile
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    // Mobile tooltips are often wider due to responsive design
    const estimatedTooltipWidth = Math.min(300, viewportWidth - 32); // Account for mobile padding
    const estimatedTooltipHeight = size === 'auto' ? 40 : 120; // Smaller estimate for auto-size tooltips
    const tooltipWidth = tooltipRect?.width && tooltipRect.width > 0 ? tooltipRect.width : estimatedTooltipWidth;
    const tooltipHeight = tooltipRect?.height && tooltipRect.height > 0 ? tooltipRect.height : estimatedTooltipHeight;

    // Determine vertical position based on available space (mobile-optimized)
    let finalPosition = preferredPosition;
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const viewportCenterY = viewportHeight / 2;

    // For mobile, prefer positioning based on which half of screen trigger is in
    if (triggerCenterY < viewportCenterY) {
      // Upper half of screen - prefer below
      if (spaceBelow > tooltipHeight + offset + VIEWPORT_PADDING) {
        finalPosition = 'below';
      } else if (spaceAbove > tooltipHeight + offset + VIEWPORT_PADDING) {
        finalPosition = 'above';
      }
    } else {
      // Lower half of screen - prefer above
      if (spaceAbove > tooltipHeight + offset + VIEWPORT_PADDING) {
        finalPosition = 'above';
      } else if (spaceBelow > tooltipHeight + offset + VIEWPORT_PADDING) {
        finalPosition = 'below';
      }
    }

    // Calculate horizontal position based on strategy
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const mobilePadding = 16;
    let left: number;
    let calculatedArrowOffset: number;

    if (positionStrategy === 'trigger-relative') {
      // Position tooltip centered on trigger (like desktop behavior)
      left = triggerCenterX - tooltipWidth / 2;

      // Adjust for viewport boundaries
      if (left < mobilePadding) {
        left = mobilePadding;
      } else if (left + tooltipWidth > viewportWidth - mobilePadding) {
        left = viewportWidth - mobilePadding - tooltipWidth;
      }

      // Calculate arrow offset: distance from tooltip center to trigger center
      const tooltipCenterX = left + tooltipWidth / 2;
      calculatedArrowOffset = triggerCenterX - tooltipCenterX;

      // Clamp arrow offset to keep it within reasonable bounds of tooltip
      const maxArrowOffset = tooltipWidth / 2 - 20; // 20px padding from edges
      calculatedArrowOffset = Math.max(-maxArrowOffset, Math.min(maxArrowOffset, calculatedArrowOffset));
    } else {
      // Default viewport-centered behavior (preserves existing logic)
      const idealLeft = (viewportWidth - tooltipWidth) / 2;
      left = Math.max(mobilePadding, Math.min(idealLeft, viewportWidth - tooltipWidth - mobilePadding));

      // Calculate arrow offset: how far from tooltip center the trigger is
      const tooltipCenterX = left + tooltipWidth / 2;
      calculatedArrowOffset = triggerCenterX - tooltipCenterX;

      // Clamp arrow offset to keep it within reasonable bounds of tooltip
      const maxArrowOffset = tooltipWidth / 2 - 20; // 20px padding from edges
      calculatedArrowOffset = Math.max(-maxArrowOffset, Math.min(maxArrowOffset, calculatedArrowOffset));
    }

    // Calculate vertical position
    let top: number;
    if (finalPosition === 'above') {
      top = triggerRect.top - tooltipHeight - offset;
    } else {
      top = triggerRect.bottom + offset;
    }

    return {
      position: finalPosition,
      coordinates: { top, left },
      arrowOffset: calculatedArrowOffset,
    };
  }, [preferredPosition, offset, positionStrategy]);

  // Open tooltip
  const openTooltip = useCallback(() => {
    setIsOpen(true);
    setPosition(preferredPosition);
    setCoordinates({ top: 0, left: 0 }); // Temporary coordinates

    // Recalculate position after state update
    requestAnimationFrame(() => {
      const calculation = calculatePosition();
      if (calculation) {
        setPosition(calculation.position);
        setCoordinates(calculation.coordinates);
        setArrowOffset(calculation.arrowOffset);
      }
    });
  }, [preferredPosition, calculatePosition]);

  // Close tooltip
  const closeTooltip = useCallback(() => {
    setIsOpen(false);
    setCoordinates(null);
    setArrowOffset(0);
  }, []);

  // Unified mobile click handler - industry standard approach for all mobile browsers
  const handleMobileClick = useYieldCallback(
    (event: React.MouseEvent) => {
      // Simple click handling - browser manages touch-to-click conversion
      event.stopPropagation(); // Prevent card selection

      if (isOpen) {
        closeTooltip();
      } else {
        openTooltip();
      }
    },
    [isOpen, openTooltip, closeTooltip],
  );

  // Handle keyboard activation (Enter/Space)
  const handleKeyDown = useYieldCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (isOpen) {
          closeTooltip();
        } else {
          openTooltip();
        }
      }
    },
    [isOpen, openTooltip, closeTooltip],
  );

  // Handle tap outside to close with safe tap (prevents unwanted navigation)
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    const handleTapOutside = (event: TouchEvent) => {
      const target = event.target as Node;

      // Check if tap is outside both tooltip and trigger
      if (
        tooltipRef.current &&
        triggerRef.current &&
        !tooltipRef.current.contains(target) &&
        !triggerRef.current.contains(target)
      ) {
        // SAFE TAP: Prevent this tap from doing anything else
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        closeTooltip();
      }
    };

    // Use capture phase to intercept events before other handlers
    const addListener = () => {
      document.addEventListener('touchstart', handleTapOutside, {
        capture: true, // Intercept in capture phase before other handlers
        passive: false, // Allow preventDefault to work
      });
    };

    // Add listener with standard timing for mobile devices
    const delay = 100; // Standard delay for mobile devices
    const timeoutId = setTimeout(addListener, delay);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('touchstart', handleTapOutside, true); // Remove with capture = true
    };
  }, [isOpen, closeTooltip]);

  // Handle scroll repositioning and auto-close with multi-source scroll detection
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    let animationFrame: number;

    const handleScroll = () => {
      if (!triggerRef.current) return;

      // Use requestAnimationFrame for smooth 60fps repositioning
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(() => {
        if (!triggerRef.current) return;

        // Always recalculate position to follow trigger during scroll
        const calculation = calculatePosition();
        if (calculation) {
          setPosition(calculation.position);
          setCoordinates(calculation.coordinates);
          setArrowOffset(calculation.arrowOffset);
        }

        // Check if trigger is way out of view and close if needed
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Close only when trigger is completely out of viewport (with buffer)
        if (triggerRect.bottom < -100 || triggerRect.top > viewportHeight + 100) {
          closeTooltip();
        }
      });
    };

    // Find all potential scroll sources for this trigger
    const scrollParent = getScrollParent(triggerRef.current);
    const scrollSources: (Element | Document | Window)[] = [
      document, // Document-level scrolling (SRP)
      scrollParent, // Container scrolling (Homehub/HDP)
      window, // Window resize
    ].filter(
      (source, index, arr) =>
        // Remove duplicates (e.g., if scrollParent is document.documentElement)
        source && arr.indexOf(source) === index,
    );

    // Add scroll listeners to all potential scroll sources
    scrollSources.forEach((source) => {
      if (source === window) {
        source.addEventListener('resize', handleScroll);
      } else {
        source.addEventListener('scroll', handleScroll, { passive: true });
      }
    });

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      // Remove listeners from all sources
      scrollSources.forEach((source) => {
        if (source === window) {
          source.removeEventListener('resize', handleScroll);
        } else {
          source.removeEventListener('scroll', handleScroll);
        }
      });
    };
  }, [isOpen, closeTooltip, calculatePosition]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    isOpen,
    position,
    coordinates,
    arrowOffset,

    // Refs
    triggerRef,
    tooltipRef,

    // Handlers
    handleMobileClick,
    handleKeyDown,

    // Manual controls
    openTooltip,
    closeTooltip,
  };
};
